import type { ApolloError, FetchResult } from '@apollo/client';
import { gql, useMutation } from '@apollo/client';
import { EmailAddress, Num, Obj, Str } from '@livecontrol/core-utils';
import { Phone } from '@livecontrol/locale-us';
import type { Contact} from '@livecontrol/scheduler/model';
import { Account, Location } from '@livecontrol/scheduler/model';
import { useCallback, useState } from 'react';
import { Errors } from '../graphql';
import type { MutationResult } from '../graphql';
import * as Dbx from './dbx';
import { QUERY } from './use-contacts';

interface TVariables {
  first_name?: string;
  last_name?: string;
  email?: string;
  phone_number?: string;
  user?: number;
  notes?: string;
  phone_type?: string;
  location?: string;
  preferred_contact_method?: string;
  priority?: number;
}

interface TData {
  createContact: Dbx.Record;
}

type Args = Pick<Contact, 'email' | 'firstName' | 'lastName' | 'location' | 'notes' | 'phone' | 'phoneType' | 'preferredContactMethod' | 'priority'> & {
  account: Account.Like;
};

const MUTATION = gql`
  mutation CreateContact($input: CreateContactInput!) {
    createContact(input: $input) {
      ...StandardContactResponse
    }
  }
  ${Dbx.StandardContactResponse}
`;

export const useCreate = (): [
  (args: Args) => Promise<Contact | undefined>,
  MutationResult<Contact, 'contact'>
] => {
  const [mutation, result] = useMutation<TData, { input: TVariables }>(MUTATION);

  const [error, setError] = useState<Error | undefined>();
  const [contact, setContact] = useState<Contact | undefined>();

  return [
    useCallback(
      async (args: Args): Promise<Contact | undefined> => {
        let contact_: Contact | undefined;

        try {
          // Parse the input arguments
          const input = {
            first_name: Str.normalize(args.firstName),
            last_name: Str.normalize(args.lastName),
            email: EmailAddress.normalize(args.email),
            phone_number: Phone.normalize(args.phone)?.format(),
            user: Account.toId(args.account)
          };

          if (!Obj.isHydrated(input)) {
            throw Errors.badRequest();
          }


          // Execute the GraphQL mutation
          const response = await mutation({
            variables: { input: {
                ...input,
                notes: Str.normalize(args.notes) ?? '',
                location:Location.toId(args.location?.id),
                phone_type: Str.normalize(args.phoneType),
                preferred_contact_method: Str.normalize(args.preferredContactMethod),
                priority: Num.normalize( args.priority)
              }
            },
            refetchQueries: [
              {
                query: QUERY,
                variables: { id: input.user }
              }
            ]
          })
            .then(({ data }: FetchResult<TData>) => data?.createContact)
            .catch((_error: ApolloError) => {
              throw Errors.serverError();
            });

          // Parse the server response
          contact_ = Dbx.normalize(response);

          if (!contact_) {
            throw Errors.serverError();
          }
        } catch (error_: unknown) {
          setError(<Error>error_);
        }

        setContact(contact_);

        return contact_;
      },
      [mutation, setError, setContact]
    ),
    {
      contact,
      error,
      called: result.called,
      loading: result.loading
    }
  ];
};
